import { Component } from '@theme/component';

/**
 * `<bundle-bottom-bar>` — viewport-pinned summary with collapsed + expanded
 * states (Figma 384:2591 / 384:5491).
 *
 * Collapsed (default): title + price + "View Bundle". Expanded: one slot per
 * step ("Choose Your X" → filled product card on save) + title/price + close.
 * Price is committed/lowest-config from `bundle:update`. Pure presentation —
 * mutates state only through the builder's declarative bindings.
 */
export class BundleBottomBarComponent extends Component {
  #builder = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#builder = /** @type {any} */ (this.closest('bundle-builder'));
    this.#builder?.addEventListener('bundle:update', this.#onUpdate);
    this.refs.viewBtn?.addEventListener('click', this.#expand);
    this.refs.close?.addEventListener('click', this.#collapse);
    this.addEventListener('click', this.#onSlotClick);

    this.#resizeObserver = new ResizeObserver(this.#reserveSpace);
    this.#resizeObserver.observe(this);
    this.#reserveSpace();
    this.#builder?.requestUpdate?.();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#builder?.removeEventListener('bundle:update', this.#onUpdate);
    this.refs.viewBtn?.removeEventListener('click', this.#expand);
    this.refs.close?.removeEventListener('click', this.#collapse);
    this.removeEventListener('click', this.#onSlotClick);
    this.#resizeObserver?.disconnect();
    this.#builder?.style.removeProperty('--bb-bar-height');
  }

  #expand = () => {
    this.removeAttribute('data-collapsed');
    this.#reserveSpace();
  };

  #collapse = () => {
    this.setAttribute('data-collapsed', '');
    this.#reserveSpace();
  };

  /** Reserve the bar's real height on the builder so it never covers content. */
  #reserveSpace = () => {
    this.#builder?.style.setProperty('--bb-bar-height', `${this.offsetHeight}px`);
  };

  /**
   * Expanded-drawer slot tap → get out of the way and show the step.
   *
   * The slot's own `on:click="bundle-builder/goToStep?step=N"` already performs
   * the navigation, and component.js delegates those bindings from `document`
   * in the CAPTURE phase — so the step has already changed by the time this
   * bubble-phase listener runs. That leaves only the drawer's own concern:
   * without it the expanded bar stays open over the picker it just navigated
   * to, and the tap reads as a no-op.
   * @param {Event} event
   */
  #onSlotClick = (event) => {
    const target = event.target;
    const slot = target instanceof Element ? target.closest('.bb-bar__slot') : null;
    if (!slot) return;

    // Collapse BEFORE measuring. Expanded, the bar is ~280px taller than
    // collapsed, and `#collapse` re-reserves `--bb-bar-height` — the builder's
    // bottom padding, and so the page's height. Scrolling first would aim at a
    // layout that the collapse then invalidates (and `goToStep`'s own
    // scroll-to-builder-top, fired during capture, did exactly that).
    this.#collapse();
    this.#scrollToStep(Number(slot.getAttribute('data-step')));
  };

  /**
   * Put a step's picker at the top of the viewport. The sticky-header offset
   * lives in CSS as `scroll-margin-block-start` on `bundle-step`
   * (bundle-builder-v2.css), matching how the theme offsets its other anchor
   * targets.
   *
   * Reduced motion gets `'instant'`, not `'auto'`: base.css sets
   * `html { scroll-behavior: smooth }` globally, and `'auto'` defers to exactly
   * that CSS — so it would animate anyway. `'instant'` is the only value that
   * actually honours the preference here.
   * @param {number} index
   */
  #scrollToStep(index) {
    const step = this.#builder?.querySelector(`bundle-step[data-step="${index}"]:not([hidden])`);
    if (!step) return;
    const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    step.scrollIntoView({ behavior: reduceMotion ? 'instant' : 'smooth', block: 'start' });
  }

  /** @param {CustomEvent} event */
  #onUpdate = (event) => {
    const builder = this.#builder;
    if (!builder) return;
    const { totals } = event.detail;

    // ── Price (synced across both collapsed + expanded summaries) ──
    const hasSavings = totals.savings > 0;
    for (const el of this.querySelectorAll('.bb-bar__total')) el.textContent = builder.money(totals.total);
    for (const el of this.querySelectorAll('.bb-bar__compare')) {
      el.textContent = hasSavings ? builder.money(totals.subtotal) : '';
      el.toggleAttribute('hidden', !hasSavings);
    }
    for (const el of this.querySelectorAll('.bb-bar__savings')) {
      el.textContent = totals.percent > 0 ? `SAVE ${totals.percent}%` : '';
      el.toggleAttribute('hidden', totals.percent <= 0);
    }

    // ── Slots (committed selection → filled card) ──
    const slots = this.#asArray(this.refs.slots);
    const thumbs = this.#asArray(this.refs.slotThumb);
    const filled = this.#asArray(this.refs.slotFilled);
    const values = this.#asArray(this.refs.slotValue);

    slots.forEach((slot, i) => {
      const stepIndex = Number(slot.dataset.step);
      const sel = builder.getStepSelection(stepIndex);
      const product = sel?.product ?? null;
      const isFilled = Boolean(product);

      slot.toggleAttribute('data-filled', isFilled);
      slot.querySelector('.bb-bar__slot-empty')?.toggleAttribute('hidden', isFilled);
      filled[i]?.toggleAttribute('hidden', !isFilled);

      if (values[i]) {
        // Name the chosen VARIANT too when the product offers a real choice
        // (e.g. the kids bags' size) — otherwise the summary can't distinguish
        // the two differently-priced lines the bar is already pricing.
        values[i].textContent = product ? `${sel.step.label}: ${product.color || product.title}${this.#variantSuffix(sel)}` : '';
      }

      const thumb = thumbs[i];
      if (thumb) {
        const img = thumb.querySelector('img');
        const src = product?.image || product?.swatch || '';
        if (img && src) {
          img.src = src;
          img.alt = product?.title ?? '';
        }
        thumb.toggleAttribute('hidden', !isFilled || !src);
      }
    });

    // Bundle complete = every step has a committed product. Drives the mobile
    // Checkout CTA, which is only shown (via CSS) when `[data-complete]`.
    const steps = builder.config?.steps ?? [];
    const allChosen = steps.length > 0 && steps.every((_, i) => Boolean(builder.getStepSelection(i)?.product));
    this.toggleAttribute('data-complete', allChosen);
    // "Checkout" vs "Add to Cart", per the section's atc_mode setting.
    if (this.refs.checkoutBtn instanceof HTMLElement && builder.ctaLabel) {
      this.refs.checkoutBtn.textContent = builder.ctaLabel;
    }
    // The CTA changes the bar's height; re-reserve so content isn't covered.
    this.#reserveSpace();
  };

  /**
   * " — 9-12 years old 31” (H) 8” (W)" for a multi-variant product, empty for a
   * single-variant one (where there is nothing to disambiguate).
   * @param {{product:any, variant:any}} sel
   */
  #variantSuffix(sel) {
    if ((sel?.product?.variants?.length ?? 0) < 2 || !sel?.variant) return '';
    const options = Array.isArray(sel.variant.options) ? sel.variant.options.filter(Boolean) : [];
    const label = options.length ? options.join(' / ') : sel.variant.title;
    return label ? ` — ${label}` : '';
  }

  /** @param {Element | Element[] | undefined} ref @returns {Element[]} */
  #asArray(ref) {
    if (!ref) return [];
    return Array.isArray(ref) ? ref : [ref];
  }
}

if (!customElements.get('bundle-bottom-bar')) {
  customElements.define('bundle-bottom-bar', BundleBottomBarComponent);
}
